import React, { useEffect, useId, useState } from "react";
import { labelClass } from "../../../constants/formStyles.ts";
import ClientsService from "../../../services/ClientsService/ClientsService.ts";
import SelectGroup from "../SelectGroup/SelectGroup.tsx";

type Options = Record<string, string>;

type BackendLocation = {
  ekatte: string;
  districtCode: string;
  districtName: string;
  municipalityCode: string;
  municipalityName: string;
  settlementName: string;
};

type Props = {
  jwt: string;
  value: string;
  onChange: (ekatte: string) => void;
};

// The client and lifecycle screens use the same PHP nomenclature endpoints. Cache
// requests because owner, policyholder and driver often share one address.
const areasCache = new Map<string, Promise<Options>>();
const municipalitiesCache = new Map<string, Promise<Options>>();
const citiesCache = new Map<string, Promise<Options>>();
const locationCache = new Map<string, Promise<BackendLocation>>();

const asOptions = (value: string | object): Options => value as Options;
const withoutPrompt = (options: Options): Options =>
  Object.fromEntries(Object.entries(options).filter(([key]) => key !== "0"));

const cached = (
  cache: Map<string, Promise<Options>>,
  key: string,
  loader: () => Promise<string | object>
): Promise<Options> => {
  const existing = cache.get(key);
  if (existing) return existing;

  const request = loader()
    .then(asOptions)
    .then(withoutPrompt)
    .catch((error) => {
      cache.delete(key);
      throw error;
    });
  cache.set(key, request);
  return request;
};

const getAreas = (jwt: string) =>
  cached(areasCache, "areas", () => ClientsService.getAllAreas(jwt));
const getMunicipalities = (jwt: string, district: string) =>
  cached(municipalitiesCache, district, () =>
    ClientsService.getAllMunicipalities(jwt, district)
  );
const getCities = (jwt: string, municipality: string) =>
  cached(citiesCache, municipality, () =>
    ClientsService.getAllCities(jwt, municipality)
  );
const getLocation = (jwt: string, ekatte: string): Promise<BackendLocation> => {
  const key = ekatte;
  const existing = locationCache.get(key);
  if (existing) return existing;

  const request = ClientsService.getLocationByEkatte(jwt, ekatte)
    .then((location) => location as BackendLocation)
    .catch((error) => {
      locationCache.delete(key);
      throw error;
    });
  locationCache.set(key, request);
  return request;
};

/** Backend-driven Област → Община → Населено място selector. */
const BackendLocationSelect: React.FC<Props> = ({ jwt, value, onChange }) => {
  const id = useId();
  const [districts, setDistricts] = useState<Options>({});
  const [municipalities, setMunicipalities] = useState<Options>({});
  const [cities, setCities] = useState<Options>({});
  const [district, setDistrict] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!jwt) return;
    let cancelled = false;
    setLoading(true);
    getAreas(jwt)
      .then((options) => {
        if (!cancelled) setDistricts(options);
      })
      .catch(() => {
        if (!cancelled) setError("Неуспешно зареждане на областите.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jwt]);

  // Resolve the two parent levels for registry and saved policy values. This is a
  // local backend concern; lifecycle still stores and sends only the EKATTE code.
  useEffect(() => {
    if (!jwt || !value) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    getLocation(jwt, value)
      .then((location) => {
        if (cancelled) return;
        if (!location?.districtCode || !location?.municipalityCode) {
          setError("Населеното място не е намерено.");
          return;
        }
        setDistrict(location.districtCode);
        setMunicipality(location.municipalityCode);
      })
      .catch(() => {
        if (!cancelled) setError("Неуспешно разпознаване на населеното място.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, value]);

  useEffect(() => {
    if (!district) {
      setMunicipalities({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    setMunicipalities({});
    getMunicipalities(jwt, district)
      .then((options) => {
        if (!cancelled) setMunicipalities(options);
      })
      .catch(() => {
        if (!cancelled) setError("Неуспешно зареждане на общините.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [district, jwt]);

  useEffect(() => {
    if (!municipality) {
      setCities({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    setCities({});
    getCities(jwt, municipality)
      .then((options) => {
        if (!cancelled) setCities(options);
      })
      .catch(() => {
        if (!cancelled) setError("Неуспешно зареждане на населените места.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, municipality]);

  const changeDistrict = (next: string) => {
    setDistrict(next);
    setMunicipality("");
    setMunicipalities({});
    setCities({});
    onChange("");
  };

  const changeMunicipality = (next: string) => {
    setMunicipality(next);
    setCities({});
    onChange("");
  };

  const changeCity = (next: string) => {
    onChange(next);
  };

  return (
    <div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <label htmlFor={`${id}-district`} className={labelClass}>
            Област
          </label>
          <SelectGroup
            id={`${id}-district`}
            value={district}
            onChange={changeDistrict}
            options={districts}
            placeholder="Изберете Област"
            disabled={loading && !Object.keys(districts).length}
          />
        </div>
        <div>
          <label htmlFor={`${id}-municipality`} className={labelClass}>
            Община
          </label>
          <SelectGroup
            id={`${id}-municipality`}
            value={municipality}
            onChange={changeMunicipality}
            options={municipalities}
            placeholder="Изберете Община"
            disabled={!district}
          />
        </div>
        <div>
          <label htmlFor={`${id}-city`} className={labelClass}>
            Населено място
          </label>
          <SelectGroup
            id={`${id}-city`}
            value={cities[value] ? value : ""}
            onChange={changeCity}
            options={cities}
            placeholder="Изберете Населено място"
            disabled={!municipality}
          />
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
};

export default BackendLocationSelect;
